/**
 * @module composables/useConfirmDialog
 *
 * Centralized confirmation dialog composable. Provides a singleton `confirm()`
 * function that returns a Promise<boolean>, replacing the many scattered
 * v-dialog confirmations across the codebase with a single reusable pattern.
 *
 * Usage:
 *   const { confirm } = useConfirmDialog();
 *   const ok = await confirm({ title: 'Delete?', text: 'Are you sure?' });
 *   if (ok) { ... }
 *
 * The ConfirmDialog.vue component (mounted once in App.vue) reads from these
 * refs and resolves the promise when the user confirms or cancels.
 */

import { ref } from "vue";

// ── Singleton state (shared across all consumers) ────────────────────

/** Whether the confirm dialog is currently visible */
const show = ref(false);

/** Dialog title text */
const title = ref("");

/** Dialog body text (main message) */
const text = ref("");

/** Label for the confirm button (defaults to "Confirm") */
const confirmText = ref("Confirm");

/** Color for the confirm button (defaults to "error") */
const confirmColor = ref("error");

/**
 * Optional alert object to display inside the dialog card.
 * When set, renders a v-alert with { type, text } props.
 * Used for warnings like "This action cannot be undone."
 */
const alert = ref(null);

/**
 * The resolve function for the current pending promise.
 * Called with true (confirm) or false (cancel) when the user acts.
 */
let _resolve = null;

/**
 * confirm - Opens the confirmation dialog and returns a Promise that
 * resolves to true (user confirmed) or false (user cancelled).
 *
 * @param {Object} options - Dialog configuration
 * @param {string} options.title - Dialog title
 * @param {string} options.text - Dialog body text
 * @param {string} [options.confirmText="Confirm"] - Confirm button label
 * @param {string} [options.confirmColor="error"] - Confirm button color
 * @param {Object} [options.alert=null] - Optional v-alert { type, text }
 * @returns {Promise<boolean>} Resolves true on confirm, false on cancel
 */
const confirm = (options) => {
  // Set all dialog properties from options
  title.value = options.title || "Confirm";
  text.value = options.text || "";
  confirmText.value = options.confirmText || "Confirm";
  confirmColor.value = options.confirmColor || "error";
  alert.value = options.alert || null;

  // Show the dialog
  show.value = true;

  // Return a promise that will be resolved by handleConfirm or handleCancel
  return new Promise((resolve) => {
    _resolve = resolve;
  });
};

/**
 * handleConfirm - Called by ConfirmDialog.vue when the user clicks confirm.
 * Closes the dialog and resolves the pending promise with true.
 */
const handleConfirm = () => {
  show.value = false;
  if (_resolve) {
    _resolve(true);
    _resolve = null;
  }
};

/**
 * handleCancel - Called by ConfirmDialog.vue when the user clicks cancel
 * or the dialog is dismissed. Resolves the pending promise with false.
 */
const handleCancel = () => {
  show.value = false;
  if (_resolve) {
    _resolve(false);
    _resolve = null;
  }
};

/**
 * Composable function that returns the shared confirm dialog state.
 * Because all refs are module-scoped singletons, every consumer gets
 * the same reactive references.
 */
export function useConfirmDialog() {
  return {
    // Public API for consumers
    confirm,

    // Internal state and handlers (used by ConfirmDialog.vue)
    show,
    title,
    text,
    confirmText,
    confirmColor,
    alert,
    handleConfirm,
    handleCancel,
  };
}
